export type Metric = {
  value: string;
  label: string;
};

export type ThesisPillar = {
  index: string;
  title: string;
  body: string;
};

export type Partner = {
  name: string;
  type: string;
  capital: string;
  focus: string;
  location: string;
  description: string;
  website?: string;
};

export type TeamMember = {
  name: string;
  role: string;
  company: string;
  image?: string;
  bio: string;
  focus: string;
  highlights: string[];
  linkedin?: string;
};

export type PortfolioCompany = {
  name: string;
  sector: string;
  logo: string;
  description: string;
  stage: string;
  website?: string;
};

export type NewsroomPost = {
  slug: string;
  company: string;
  title: string;
  excerpt: string;
  date: string;
  category: string;
  sector: string;
  backing: string;
  focus: string;
  companySite?: string;
};

export type TechPartner = {
  name: string;
  category: string;
};

export type ProcessStep = {
  step: string;
  title: string;
  duration: string;
  body: string;
};

export type ProgramPlan = {
  stage: string;
  equity: string;
  capacity: string;
  bestFor: string;
  support: string[];
};

export type EcosystemCompany = {
  name: string;
  sector: string;
  description: string;
  profile: string;
};

export type EcosystemGroup = {
  title: string;
  note: string;
  companies: EcosystemCompany[];
};

export type SupportCategory = {
  name: string;
  count: string;
  examples: string;
};

export type FAQ = {
  question: string;
  answer: string;
};

export const company = {
  displayName: "RedQueen Fund",
  legalName: "Fundacja Regina Purpurea Fundus",
  krs: "0000223158",
  nip: "9512152516",
  regon: "140132635",
  founded: "Registered in 2004",
  address: "Poligonowa 1/73, 04-051 Warszawa",
  email: "info@redqueenfund.com",
  logo: "/logos/regina-purpurea-fundus-cream.png",
  tagline: "Strategic partner of Odra Venture"
};

export const metrics: Metric[] = [
  { value: "2004", label: "Foundation registration year" },
  { value: "$200M+", label: "Aggregate platform experience" },
  { value: "890+", label: "Technology grant pathways" },
  { value: "$2M", label: "Maximum program support capacity" }
];

export const thesisPillars: ThesisPillar[] = [
  {
    index: "01",
    title: "Capital plus operating leverage",
    body:
      "RedQueen works with Odra Venture to connect founders with investment partners, technology credits, pilot customers, strategic operators, and the practical support needed after the first cheque."
  },
  {
    index: "02",
    title: "AI, infrastructure, and high-consequence markets",
    body:
      "The shared network focuses on AI infrastructure, cybersecurity, energy systems, climate tooling, data operations, and enterprise software where speed and trust both matter."
  },
  {
    index: "03",
    title: "Syndicate-first company building",
    body:
      "We help founders design the next financing step, prepare materials, open partner conversations, and coordinate with a consortium that can add more than one kind of value."
  },
  {
    index: "04",
    title: "European roots, international ambition",
    body:
      "The network is anchored in Poland and Central Europe while helping startups prepare for customers, capital, and partners across Europe, the United States, and strategic global markets."
  }
];

export const programPlans: ProgramPlan[] = [
  {
    stage: "Pre-seed",
    equity: "7% equity",
    capacity: "up to $200,000",
    bestFor: "Founders moving from prototype, early signal, or first pilots into a fundable company formation path.",
    support: ["Company formation readiness", "MVP and pilot planning", "Grant and cloud stack", "First investor narrative"]
  },
  {
    stage: "Seed",
    equity: "up to 12% equity",
    capacity: "up to $500,000",
    bestFor: "Teams with a live product, early revenue, or clear customer pull that need capital and commercial leverage.",
    support: ["Syndicate design", "Enterprise introductions", "Hiring and operating cadence", "Follow-on round materials"]
  },
  {
    stage: "Series A",
    equity: "15-20% equity",
    capacity: "up to $2M",
    bestFor: "Companies preparing institutional scale, larger partnerships, and disciplined growth-stage investor conversations.",
    support: ["Board-level story", "International partner access", "Data room preparation", "Strategic infrastructure support"]
  }
];

export const supportCategories: SupportCategory[] = [
  { name: "Web Development", count: "147", examples: "Framer, Retool, Bubble, Stripe" },
  { name: "AI Software", count: "130", examples: "OpenAI, model tooling, automation platforms" },
  { name: "Marketing", count: "117", examples: "HubSpot, Mailchimp, growth systems" },
  { name: "Data & Analytics", count: "104", examples: "Datadog, Mixpanel, MongoDB" },
  { name: "Sales", count: "81", examples: "CRM, enrichment, outbound tooling" },
  { name: "Legal, Finance & HR", count: "107", examples: "Legal, accounting, payroll, people ops" }
];

export const ecosystemGroups: EcosystemGroup[] = [
  {
    title: "Venture and technology exposure",
    note:
      "Selected companies from the Odra/RPF relationship map where the network has investment, advisory, sourcing, or historical exposure. This section is not a direct-investment list.",
    companies: [
      {
        name: "Cardiobotics",
        sector: "MedTech / Robotics",
        profile: "VC",
        description:
          "Autonomous robotic systems for cardiovascular diagnostics and minimally invasive medical workflows."
      },
      {
        name: "Optigis",
        sector: "GIS / Geodata",
        profile: "VC",
        description:
          "Geographic information systems and digital cartography for smart-city management and logistics."
      },
      {
        name: "Call2Action",
        sector: "MarTech / Digital Media",
        profile: "VC",
        description:
          "Consumer engagement technology using real-time behavioral data for digital campaign optimization."
      },
      {
        name: "Maple Bear Holdings Polska",
        sector: "EdTech / Franchise",
        profile: "VC",
        description:
          "Technology-supported bilingual education franchise operations connected to the Maple Bear system."
      },
      {
        name: "Arvena",
        sector: "Sustainable Materials",
        profile: "VC",
        description:
          "Biodegradable barrier papers and bio-based materials for lower-impact packaging applications."
      },
      {
        name: "VivaDrive",
        sector: "Mobility / AI",
        profile: "VC",
        description:
          "AI platform for fleet management and electric-vehicle transition planning."
      }
    ]
  },
  {
    title: "Private markets and operating network",
    note:
      "Selected private-equity and operating relationships that show the breadth of sectors around the founder and partner network.",
    companies: [
      {
        name: "HYPERHUB",
        sector: "IT & Data Infrastructure",
        profile: "PE",
        description:
          "Software development, IT consultancy, and high-performance data-processing infrastructure."
      },
      {
        name: "EULER",
        sector: "Critical Systems Software",
        profile: "PE",
        description:
          "Mission-critical software systems for public administration, finance, and large-scale infrastructure."
      },
      {
        name: "Power Mentor",
        sector: "Professional Mentoring",
        profile: "PE",
        description:
          "Digital platform connecting business professionals with senior mentors for strategic growth."
      },
      {
        name: "Energetia",
        sector: "CleanTech / Solar",
        profile: "PE",
        description:
          "Commercial and residential photovoltaic design, implementation, and renewable-energy services."
      },
      {
        name: "Carat&Co.",
        sector: "Luxury Commerce",
        profile: "PE",
        description:
          "Premium jewelry and luxury commerce experience connected to the broader founder network."
      },
      {
        name: "EASYSENT",
        sector: "FinTech",
        profile: "PE",
        description:
          "International payment operations and currency-exchange systems across European jurisdictions."
      }
    ]
  },
  {
    title: "Institutional and controlled venture initiatives",
    note:
      "Selected controlled, chamber, or ecosystem-building initiatives relevant to market access and international collaboration.",
    companies: [
      {
        name: "Veya",
        sector: "Consumer SaaS",
        profile: "Controlled venture",
        description:
          "Digital life-guidance platform combining multiple astrology systems into a subscription product."
      },
      {
        name: "PICC",
        sector: "Israel-Poland Trade",
        profile: "NGO",
        description:
          "Polish-Israeli commercial bridge for startup, R&D, venture, and market-access relationships."
      },
      {
        name: "ASEAN Business Council",
        sector: "International Trade",
        profile: "NGO",
        description:
          "Platform for economic cooperation and business missions between Poland and Southeast Asia."
      },
      {
        name: "SAFTA Business Council",
        sector: "International Trade",
        profile: "NGO",
        description:
          "Business development council oriented around South Asian market access and investment pathways."
      },
      {
        name: "Zenith Allure",
        sector: "Blockchain / Jewelry",
        profile: "Controlled venture",
        description:
          "Fine jewelry authentication and digital provenance concept for high-value luxury assets."
      },
      {
        name: "Shieldwear",
        sector: "Fashion-Tech / Security",
        profile: "Controlled venture",
        description:
          "Protective apparel concept focused on civilian ballistic clothing and premium safety products."
      }
    ]
  }
];

export const partners: Partner[] = [
  {
    name: "VO2 Ventures",
    type: "Consortium Partner",
    capital: "86.8M PLN",
    focus: "SaaS, AI, FinTech, BioTech",
    location: "Warsaw",
    website: "https://vo2ventures.pl",
    description:
      "Early-stage fund backed by PFR Ventures, focused on founders building global companies from Central Europe."
  },
  {
    name: "Space Bridge Fund",
    type: "Consortium Partner",
    capital: "25M PLN",
    focus: "Space, AI, Robotics",
    location: "Lublin",
    website: "https://www.spacebridgefund.com",
    description:
      "NCBR-backed deep tech fund supporting advanced engineering, robotics, space technology, and AI ventures."
  },
  {
    name: "DGA S.A.",
    type: "Accelerator",
    capital: "EUR 3.5M+",
    focus: "Innovation, Industry 4.0",
    location: "Poznan",
    website: "https://dga.pl",
    description:
      "Innovation consultancy and accelerator helping startups connect with enterprise and public-sector demand."
  },
  {
    name: "EdTechHub Venture",
    type: "Consortium Partner",
    capital: "80M PLN",
    focus: "EdTech, VR/AR, AI",
    location: "Lublin",
    website: "https://edtechhub.eu",
    description:
      "Knowledge technology investment network supporting digital education, immersive learning, and applied AI companies."
  },
  {
    name: "Digital Creative Cluster",
    type: "Innovation Cluster",
    capital: "500+ startups",
    focus: "Creative, Digital",
    location: "Warsaw",
    description:
      "Creative industries network with digital labs, acceleration programs, and technology infrastructure access."
  },
  {
    name: "VenturePackt",
    type: "Incubator",
    capital: "4M PLN",
    focus: "M&A, Valuation",
    location: "Wroclaw",
    website: "https://venturepackt.com",
    description:
      "Incubation and M&A advisory partner helping founders understand valuation, capital strategy, and exit readiness."
  },
  {
    name: "SpinLab Accelerator",
    type: "Accelerator",
    capital: "EUR 50K grants",
    focus: "B2B, DeepTech",
    location: "Leipzig",
    website: "https://www.spinlab.co",
    description:
      "European accelerator connected to HHL Business School, supporting B2B and deep tech founders."
  },
  {
    name: "Pointer.Capital",
    type: "Consortium Partner",
    capital: "$15M+",
    focus: "Gaming, Energy, FinTech",
    location: "Delaware, USA",
    website: "https://www.pointer.capital",
    description:
      "US-based fund and network partner across gaming, energy, fintech, and software-led market opportunities."
  },
  {
    name: "Adlytica",
    type: "Venture Builder",
    capital: "$255K+",
    focus: "AI, Infrastructure",
    location: "Wroclaw",
    website: "https://adlytica.com",
    description:
      "AI-powered venture development partner supporting startup infrastructure, automation, and enterprise-grade product systems."
  }
];

export const team: TeamMember[] = [
  {
    name: "Hubert Drabik",
    role: "Chairman",
    company: "Red Queen Investment Fund",
    image: "/team/hubert-drabik.jpg",
    focus: "Strategy and international markets",
    linkedin: "https://www.linkedin.com/in/hubertcarpediem/",
    highlights: ["Forbes Council", "Sony/HBO/MTG", "International markets"],
    bio:
      "Entrepreneur and senior executive with 30+ years of leadership across media, technology, and investment markets."
  },
  {
    name: "Shubham Kishore",
    role: "Board Member and Founder",
    company: "Odra Venture / Adlytica",
    image: "/team/shubham-kishore.jpg",
    focus: "AI and venture infrastructure",
    linkedin: "https://linkedin.com/in/shubham-kishore-adlytica/",
    highlights: ["AI automation", "Venture building", "AWS partner network"],
    bio:
      "Founder of Odra Venture and CEO of Adlytica, building AI-powered venture infrastructure for European founders."
  },
  {
    name: "Maciej Uciechowski",
    role: "Private Investor and Business Angel",
    company: "Independent",
    image: "/team/maciej-uciechowski.jpg",
    focus: "Venture capital and private equity",
    linkedin: "https://www.linkedin.com/in/maciej-uciechowski-a481b1/",
    highlights: ["VC and PE", "EU markets", "Portfolio construction"],
    bio:
      "Investor with experience across European venture capital, private equity, portfolio construction, and growth cycles."
  },
  {
    name: "Damian Puczynski",
    role: "Private Investor and Business Angel",
    company: "Independent",
    image: "/team/damian-puczynski.jpg",
    focus: "International e-commerce",
    linkedin: "https://www.linkedin.com/in/damian-puczy%C5%84ski-aa350a242/",
    highlights: ["E-commerce", "Cross-border commerce", "Digital markets"],
    bio:
      "Investor and operator with cross-border commerce experience across multiple geographies and digital markets."
  },
  {
    name: "Krzysztof Murzyn",
    role: "Technology Advisor",
    company: "Independent",
    focus: "Technology and product strategy",
    highlights: ["Tech strategy", "Product development", "Engineering leadership"],
    bio:
      "Technology executive and advisor bringing product, engineering, and technology strategy context for scaling companies."
  }
];

export const portfolio: PortfolioCompany[] = [
  {
    name: "TopSpots",
    sector: "Hospitality / Venue Tech",
    logo: "/portfolio-logos/topspots-logo.png",
    stage: "Portfolio network",
    description:
      "Venue discovery and hospitality technology for operators and customers navigating premium local experiences."
  },
  {
    name: "Bordiq",
    sector: "Cybersecurity",
    logo: "/portfolio-logos/bordiq.png",
    stage: "Seed backing",
    website: "https://bordiq.com",
    description:
      "AI-assisted cyber defense, investigation workflows, and enterprise compliance support."
  },
  {
    name: "Arcvon",
    sector: "FinTech",
    logo: "/portfolio-logos/arcvon.png",
    stage: "Portfolio network",
    description:
      "Financial technology infrastructure for modern payments, operations, and software-led finance workflows."
  },
  {
    name: "CogStorm",
    sector: "AI Infrastructure",
    logo: "/portfolio-logos/cogstorm.png",
    stage: "Seed backing",
    website: "https://cogstorm.com",
    description:
      "Runtime orchestration, observability, and governance for production multi-agent systems."
  },
  {
    name: "GridVoltX",
    sector: "Grid Software",
    logo: "/portfolio-logos/gridvoltx.png",
    stage: "Seed backing",
    website: "https://gridvoltx.com",
    description:
      "Software-defined voltage prediction, stabilization, and distributed energy orchestration."
  },
  {
    name: "CarbVault",
    sector: "Climate Infrastructure",
    logo: "/portfolio-logos/carbvault.png",
    stage: "Seed backing",
    website: "https://carbvault.com",
    description:
      "Verification, registry-grade audit trails, and reporting infrastructure for carbon markets."
  },
  {
    name: "HeatVX",
    sector: "Thermal Intelligence",
    logo: "/portfolio-logos/heatvx.png",
    stage: "Seed backing",
    website: "https://heatvx.com",
    description:
      "Predictive thermal monitoring and cooling optimization for dense compute environments."
  },
  {
    name: "SolarNerve",
    sector: "Solar Operations",
    logo: "/portfolio-logos/solarnerve.png",
    stage: "Seed backing",
    website: "https://solarnerve.com",
    description:
      "Fault prediction, asset monitoring, and autonomous solar operations software."
  },
  {
    name: "PrimVolt",
    sector: "Storage Intelligence",
    logo: "/portfolio-logos/primvolt.png",
    stage: "Seed backing",
    website: "https://primvolt.com",
    description:
      "AI-native storage intelligence for battery dispatch, lifecycle analytics, and fleet orchestration."
  },
  {
    name: "AuxGrid",
    sector: "Energy Infrastructure",
    logo: "/portfolio-logos/auxgrid.png",
    stage: "Seed backing",
    website: "https://auxgrid.com",
    description:
      "Grid forecasting, DER orchestration, and digital twin workflows for modern power systems."
  },
  {
    name: "SocWire",
    sector: "Revenue Software",
    logo: "/portfolio-logos/socwire.png",
    stage: "Seed backing",
    website: "https://socwire.com",
    description:
      "AI-native social intelligence for advocacy, publishing, listening, and revenue attribution."
  },
  {
    name: "Adlytica",
    sector: "AI Infrastructure",
    logo: "/logos/adlytica-logo.png",
    stage: "Seed backing",
    website: "https://adlytica.com",
    description:
      "AI-native marketing intelligence and venture infrastructure for modern agencies and partners."
  }
];

export const newsroomPosts: NewsroomPost[] = [
  {
    slug: "bordiq-funding-update",
    company: "Bordiq",
    companySite: "https://bordiq.com",
    title: "Bordiq announces seed backing to strengthen AI-assisted cyber defense",
    excerpt:
      "Backed by RedQueen Fund and Odra Venture, Bordiq is scaling AI-assisted threat analysis, AWS-ready deployment, and enterprise compliance workflows.",
    date: "May 5, 2026",
    category: "Portfolio Funding",
    sector: "Cybersecurity",
    backing: "RedQueen Fund and Odra Venture",
    focus: "AI-assisted threat analysis and compliance workflows"
  },
  {
    slug: "adlytica-funding-update",
    company: "Adlytica",
    companySite: "https://adlytica.com",
    title: "Adlytica announces backing to scale AI-native marketing intelligence",
    excerpt:
      "The round supports partner onboarding, client portal workflows, and AI analytics for modern agencies.",
    date: "February 2026",
    category: "Portfolio Funding",
    sector: "AI Infrastructure",
    backing: "RedQueen Fund and Odra Venture",
    focus: "Client portal, partner onboarding, and AI analytics"
  },
  {
    slug: "auxgrid-funding-update",
    company: "AuxGrid",
    companySite: "https://auxgrid.com",
    title: "AuxGrid announces backing to scale grid intelligence",
    excerpt:
      "AuxGrid is accelerating forecasting, DER orchestration, and digital twin workflows for modern grid operations.",
    date: "May 5, 2026",
    category: "Portfolio Funding",
    sector: "Energy Infrastructure",
    backing: "RedQueen Fund and Odra Venture",
    focus: "Grid forecasting, DER orchestration, and digital twin operations"
  },
  {
    slug: "carbvault-funding-update",
    company: "CarbVault",
    companySite: "https://carbvault.com",
    title: "CarbVault announces backing to advance trusted carbon market infrastructure",
    excerpt:
      "CarbVault is advancing verification, registry-grade audit trails, and enterprise reporting workflows.",
    date: "May 5, 2026",
    category: "Portfolio Funding",
    sector: "Climate Infrastructure",
    backing: "RedQueen Fund and Odra Venture",
    focus: "Verification, registry infrastructure, and climate reporting"
  },
  {
    slug: "cogstorm-funding-update",
    company: "CogStorm",
    companySite: "https://cogstorm.com",
    title: "CogStorm announces backing to move AI agents from prototype to production",
    excerpt:
      "CogStorm is investing in runtime orchestration, deployment infrastructure, observability, and governance.",
    date: "May 5, 2026",
    category: "Portfolio Funding",
    sector: "AI Infrastructure",
    backing: "RedQueen Fund and Odra Venture",
    focus: "Runtime orchestration, deployment, observability, and governance"
  },
  {
    slug: "gridvoltx-funding-update",
    company: "GridVoltX",
    companySite: "https://gridvoltx.com",
    title: "GridVoltX announces backing to accelerate software-defined voltage control",
    excerpt:
      "GridVoltX is extending prediction, stabilization, and DER orchestration for the distributed grid.",
    date: "May 5, 2026",
    category: "Portfolio Funding",
    sector: "Grid Software",
    backing: "RedQueen Fund and Odra Venture",
    focus: "Voltage prediction, stabilization, and distributed orchestration"
  },
  {
    slug: "heatvx-funding-update",
    company: "HeatVX",
    companySite: "https://heatvx.com",
    title: "HeatVX announces backing to scale thermal intelligence",
    excerpt:
      "HeatVX is accelerating predictive monitoring, cooling optimization, and deployment tooling for thermal operations.",
    date: "May 5, 2026",
    category: "Portfolio Funding",
    sector: "Thermal Intelligence",
    backing: "RedQueen Fund and Odra Venture",
    focus: "Thermal analytics, cooling optimization, and predictive operations"
  },
  {
    slug: "primvolt-funding-update",
    company: "PrimVolt",
    companySite: "https://primvolt.com",
    title: "PrimVolt announces backing to grow AI-native storage intelligence",
    excerpt:
      "PrimVolt is deepening battery dispatch, lifecycle analytics, and storage orchestration across the platform.",
    date: "May 5, 2026",
    category: "Portfolio Funding",
    sector: "Storage Intelligence",
    backing: "RedQueen Fund and Odra Venture",
    focus: "Battery dispatch, lifecycle analytics, and storage orchestration"
  },
  {
    slug: "socwire-funding-update",
    company: "SocWire",
    companySite: "https://socwire.com",
    title: "SocWire announces backing to build AI-native social intelligence",
    excerpt:
      "SocWire is accelerating employee advocacy, publishing, social listening, and revenue attribution.",
    date: "May 5, 2026",
    category: "Portfolio Funding",
    sector: "Revenue Software",
    backing: "RedQueen Fund and Odra Venture",
    focus: "Advocacy, publishing, listening, and attribution"
  },
  {
    slug: "solarnerve-funding-update",
    company: "SolarNerve",
    companySite: "https://solarnerve.com",
    title: "SolarNerve announces backing to expand autonomous solar operations",
    excerpt:
      "SolarNerve is expanding fault prediction, site automation, and performance intelligence for solar operators.",
    date: "May 5, 2026",
    category: "Portfolio Funding",
    sector: "Solar Operations",
    backing: "RedQueen Fund and Odra Venture",
    focus: "Fault prediction, asset monitoring, and plant orchestration"
  }
];

export const techPartners: TechPartner[] = [
  { name: "AWS Activate", category: "Cloud" },
  { name: "Microsoft for Startups", category: "Cloud" },
  { name: "Google Cloud", category: "Cloud" },
  { name: "Cloudflare", category: "Security" },
  { name: "OpenAI", category: "AI" },
  { name: "GitHub", category: "Developer tools" },
  { name: "Stripe", category: "Payments" },
  { name: "HubSpot", category: "Go-to-market" },
  { name: "Notion", category: "Operations" },
  { name: "Supabase", category: "Data" },
  { name: "MongoDB", category: "Data" },
  { name: "Datadog", category: "Observability" },
  { name: "Linear", category: "Product" },
  { name: "Sentry", category: "Engineering" },
  { name: "Miro", category: "Collaboration" },
  { name: "Slack", category: "Collaboration" }
];

export const processSteps: ProcessStep[] = [
  {
    step: "01",
    title: "Founder intake",
    duration: "5 minutes",
    body:
      "Submit the company, founder, traction, and fundraising context. The first pass is intentionally lightweight."
  },
  {
    step: "02",
    title: "Partner review",
    duration: "1-2 weeks",
    body:
      "The RedQueen and Odra teams review fit across thesis, timing, round structure, and the partner network that can help."
  },
  {
    step: "03",
    title: "Syndicate design",
    duration: "2-4 weeks",
    body:
      "When there is fit, we prepare the capital path, partner introductions, grant stack, and operating support plan."
  },
  {
    step: "04",
    title: "Portfolio enablement",
    duration: "Ongoing",
    body:
      "Founders receive partner access, cloud and tool credits, fundraising support, customer introductions, and operating help."
  }
];

export const faqs: FAQ[] = [
  {
    question: "Is RedQueen Fund connected to Odra Venture?",
    answer:
      "Yes. RedQueen Fund is presented here as a strategic partner of Odra Venture. The organizations coordinate around shared founder support, partner access, and selected portfolio opportunities."
  },
  {
    question: "Do you only invest money?",
    answer:
      "No. The network is designed around capital, partner syndication, cloud and software grant access, enterprise pilots, founder support, and operating help."
  },
  {
    question: "What stages are relevant?",
    answer:
      "The program matrix covers Pre-seed, Seed, and Series A. Published terms are up to $200,000 for Pre-seed at 7% equity, up to $500,000 for Seed at up to 12% equity, and up to $2M for Series A at 15-20% equity depending on the case."
  },
  {
    question: "Is the accelerator free?",
    answer:
      "It is free to apply and there is no cash program fee. Selected founders can access free or partner-provided technology grants and tooling, while equity terms still apply."
  },
  {
    question: "Which sectors fit best?",
    answer:
      "AI infrastructure, cybersecurity, energy systems, climate infrastructure, data-heavy B2B software, developer tooling, and enterprise workflows are the clearest fit."
  },
  {
    question: "How quickly do founders hear back?",
    answer:
      "The intended review window is up to two weeks. If there is a clear fit, the next step is a partner conversation and deeper materials review."
  }
];
